// Pages
import { DashboardRedirect, PortalDashboard } from '../PortalDashboard'
import { AdminOrganizations } from '../AdminOrganizations'
import { AdminReports } from '../AdminReports'
import { AdminSettings } from '../AdminSettings'
import { AdminUsers } from '../AdminUsers'
import { EventApiConfig } from '../EventApiConfig'
import { EventAttendees } from '../EventAttendees'
import { EventCheckIn } from '../EventCheckIn'
import { EventBadgeBuilder } from '../EventBadgeBuilder'
import { EventEmails } from '../EventEmails'
import { EventExhibitorDetail } from '../EventExhibitorDetail'
import { EventExhibitors } from '../EventExhibitors'
import { EventKiosk } from '../EventKiosk'
import { EventLeads } from '../EventLeads'
import { EventMaterials } from '../EventMaterials'
import { EventQualifiers } from '../EventQualifiers'
import { EventRegistration } from '../EventRegistration'
import { EventSelfRegistration } from '../EventSelfRegistration'
import { EventSessionDetail } from '../EventSessionDetail'
import { EventSessions } from '../EventSessions'
import { ExhibitorEventHome } from '../ExhibitorEventHome'
import { FetchAppLanding } from '../FetchAppLanding'
import { ExhibitorEventLicenses } from '../ExhibitorEventLicenses'
import { ExhibitorEventReports } from '../ExhibitorEventReports'
import { ExhibitorReports } from '../ExhibitorReports'
import { Login } from '../Login'
import { MultiFactorAuthentication } from '../MultiFactorAuthentication'
import { OrganizerEventHome } from '../OrganizerEventHome'
import { OrganizerEventLicenses } from '../OrganizerEventLicenses'
import { OrganizerEventReports } from '../OrganizerEventReports'
import { OrganizerReports } from '../OrganizerReports'
import { OrganizationUsers } from '../OrganizationUsers'
import { PasswordReset } from '../PasswordReset'
import { PasswordResetRequest } from '../PasswordResetRequest'
import { TrackrAccess } from '../TrackrAccess'
import { UserProfile } from '../UserProfile'

const enableTrackr = import.meta.env.VITE_TRACKR_ENABLED === 'true'

// `eeOnly` routes are unreachable by org users even by typing the URL: the global EE
// dashboard/settings, the org-level summary Reports page (EXEC-1556), and the API Config
// and Badge Builder pages the client's 7/23/26 doc denies them (EXEC-1543).
const routes = [
  // ----------------------------- Auth Routes ----------------------------- //
  {
    path: '/',
    page: Login,
    type: 'public',
  },
  {
    path: '/login',
    page: Login,
    type: 'public',
  },
  {
    path: '/verification',
    page: MultiFactorAuthentication,
    type: 'public',
  },
  {
    path: '/password-reset',
    page: PasswordReset,
    type: 'public',
  },
  {
    path: '/set-password',
    page: PasswordReset,
    type: 'public',
  },
  {
    path: '/password-reset-request',
    page: PasswordResetRequest,
    type: 'public',
  },
  // ----------------------------- General Routes ----------------------------- //
  {
    path: '/profile',
    page: UserProfile,
    type: 'private',
  },
  // ----------------------------- Admin Routes ----------------------------- //
  // The portal's single entry point. Organizers, exhibitors and users holding both land
  // on their event list here; EE users get the admin dashboard (EXEC-1550).
  {
    path: '/dashboard',
    page: PortalDashboard,
    type: 'private',
  },
  {
    path: '/users',
    page: AdminUsers,
    type: 'private',
    eeOnly: true,
  },
  {
    path: '/organizations',
    page: AdminOrganizations,
    type: 'private',
    eeOnly: true,
  },
  {
    path: '/reports',
    page: AdminReports,
    type: 'private',
    eeOnly: true,
  },
  {
    path: '/settings',
    page: AdminSettings,
    type: 'private',
    eeOnly: true,
  },
  // ----------------------------- Organization Routes ----------------------------- //
  // Superseded by /dashboard; kept so existing links and bookmarks still resolve
  {
    path: '/organization/:orgId/dashboard',
    page: DashboardRedirect,
    type: 'private',
  },
  {
    path: '/organization/:orgId/users',
    page: OrganizationUsers,
    type: 'private',
  },
  {
    path: '/organization/:orgId/reports',
    page: OrganizerReports,
    type: 'private',
    eeOnly: true,
  },
  {
    path: '/organization/:orgId/event/:eventId/home',
    page: OrganizerEventHome,
    type: 'private',
  },
  {
    path: '/organization/:orgId/event/:eventId/reports',
    page: OrganizerEventReports,
    type: 'private',
  },
  {
    path: '/organization/:orgId/event/:eventId/emails',
    page: EventEmails,
    type: 'private',
  },
  {
    path: '/organization/:orgId/event/:eventId/data-management/attendees',
    page: EventAttendees,
    type: 'private',
  },
  {
    path: '/organization/:orgId/event/:eventId/data-management/api-config',
    page: EventApiConfig,
    type: 'private',
    eeOnly: true,
  },
  {
    path: '/organization/:orgId/event/:eventId/lead-retrieval/exhibitors',
    page: EventExhibitors,
    type: 'private',
  },
  {
    path: '/organization/:orgId/event/:eventId/lead-retrieval/exhibitors/:eventExhibitorId',
    page: EventExhibitorDetail,
    type: 'private',
  },
  {
    path: '/organization/:orgId/event/:eventId/lead-retrieval/licenses',
    page: OrganizerEventLicenses,
    type: 'private',
  },
  {
    path: '/organization/:orgId/event/:eventId/check-in/settings',
    page: EventCheckIn,
    type: 'private',
  },
  {
    path: '/organization/:orgId/event/:eventId/kiosk',
    page: EventKiosk,
    type: 'private',
  },
  {
    path: '/organization/:orgId/event/:eventId/check-in/badge-builder',
    page: EventBadgeBuilder,
    type: 'private',
    eeOnly: true,
  },
  // ----------------------------- Exhibitor Routes ----------------------------- //
  // Superseded by /dashboard; kept so existing links and bookmarks still resolve
  {
    path: '/exhibitor/:orgId/dashboard',
    page: DashboardRedirect,
    type: 'private',
  },
  {
    path: '/exhibitor/:orgId/users',
    page: OrganizationUsers,
    type: 'private',
  },
  {
    path: '/exhibitor/:orgId/reports',
    page: ExhibitorReports,
    type: 'private',
  },
  {
    path: '/exhibitor/:orgId/event/:eventId/home',
    page: ExhibitorEventHome,
    type: 'private',
  },
  {
    path: '/exhibitor/:orgId/event/:eventId/leads',
    page: EventLeads,
    type: 'private',
  },
  {
    path: '/exhibitor/:orgId/event/:eventId/app-setup/materials',
    page: EventMaterials,
    type: 'private',
  },
  {
    path: '/exhibitor/:orgId/event/:eventId/app-setup/qualifiers',
    page: EventQualifiers,
    type: 'private',
  },
  {
    path: '/exhibitor/:orgId/event/:eventId/licenses',
    page: ExhibitorEventLicenses,
    type: 'private',
  },
  {
    path: '/exhibitor/:orgId/event/:eventId/reports',
    page: ExhibitorEventReports,
    type: 'private',
  },
  // ----------------------------- Event (Public) ----------------------------- //
  {
    path: '/event/:eventId/register',
    page: EventRegistration,
    type: 'custom',
  },
  {
    path: '/event/:eventId/self-register',
    page: EventSelfRegistration,
    type: 'custom',
  },
  // ----------------------------- App Landing Pages (Public) ----------------------------- //
  {
    path: '/app/fetch',
    page: FetchAppLanding,
    type: 'custom',
  },
]

// ----------------------------- Feature Flag Enabled: Organization Routes ----------------------------- //
if (enableTrackr) {
  routes.push({
    path: '/organization/:orgId/event/:eventId/sessions/list',
    page: EventSessions,
    type: 'private',
  })
  routes.push({
    path: '/organization/:orgId/event/:eventId/sessions/list/:sessionId',
    page: EventSessionDetail,
    type: 'private',
  })
  routes.push({
    path: '/organization/:orgId/event/:eventId/sessions/access',
    page: TrackrAccess,
    type: 'private',
  })
}

export default routes
